import { parseUnits } from '@ethersproject/units';
import { useQuery } from '@tanstack/react-query';
import type { CoinMode } from '@ui/hooks/useCoinMode';
import { useCoinMode } from '@ui/hooks/useCoinMode';
import { GraphQLContext } from '@ui/providers/GraphQLProvider';
import { getSnapshotsAtom } from '@ui/queries/history';
import { getQuery } from '@ui/queries/queries';
import { QueryKey } from '@ui/queries/queryKey';
import { currentWallet } from '@veno-app/wallet';
import { useContext, useMemo } from 'react';

const { useAccount } = currentWallet;

const useEarningsData = (includeLpEarnings: boolean, mode?: CoinMode) => {
  const globalCoinMode = useCoinMode();
  const coinMode = mode ?? globalCoinMode;
  const isCro = coinMode === 'cro';
  const earningsClient = useContext(GraphQLContext).historyClient;
  const exchangeRateClient = useContext(GraphQLContext).subgraphClient;
  const account = useAccount();
  const earningsQuery = getQuery(QueryKey.EARNINGS_HISTORY)(
    earningsClient,
    account ?? '',
  );
  const exchangeQuery = getQuery(QueryKey.EXCHANGE_RATE_HISTORY)(
    exchangeRateClient,
  );
  const { data: earningsData, isLoading: earningsDataLoading } = useQuery({
    ...earningsQuery,
    enabled: !!account && isCro,
  });
  const { data: exchangeRateData, isLoading: exchangeRateDataLoading } =
    useQuery({
      ...exchangeQuery,
      enabled: isCro,
    });
  const isLoading = earningsDataLoading || exchangeRateDataLoading;

  const {
    currentFerroFactor: currentFerroFactorAtom,
    currentFerroLpBalance: currentFerroLpBalanceAtom,
  } = useAtomEarningsData(!isCro, account ?? '');
  const {
    data,
    currentFerroFactor,
    currentFerroLpBalance,
    currentVvsCroLcroBalance,
    currentTLcroBalance,
  } = useMemo(() => {
    let currentFerroFactor = currentFerroFactorAtom ?? 0;
    let currentFerroLpBalance = currentFerroLpBalanceAtom ?? 0;
    let currentTLcroBalance = 0;
    let currentVvsCroLcroBalance = 0;
    if (
      !earningsData?.earningSnapshots.length ||
      !exchangeRateData?.liquidCroDailySnapshots.length ||
      !isCro
    )
      return {
        data: [],
        currentFerroFactor,
        currentFerroLpBalance,
        currentTLcroBalance,
        currentVvsCroLcroBalance: 0,
      };
    const firstDate = parseInt(
      earningsData.earningSnapshots[earningsData.earningSnapshots.length - 1]
        .date,
    );
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const nDays = (today.getTime() / 1000 - firstDate) / (3600 * 24) + 1;
    const dates = Array.from({ length: nDays })
      .map((_, i) => new Date((firstDate + i * 3600 * 24) * 1000))
      .slice(-200);
    const snapshots = earningsData.earningSnapshots.map((sn) => ({
      ...sn,
      date: parseInt(sn.date) * 1000,
    }));
    const rates = [...exchangeRateData.liquidCroDailySnapshots]
      .slice(0, dates.length)
      .reverse();

    const earnings = dates.map((d, i) => {
      const snapshot = snapshots.find((sn) => sn.date <= d.getTime());
      const updated = snapshot?.date === d.getTime();
      const ferroLPBalance = parseFloat(snapshot?.ferroLPBalance ?? '0');
      const earnedFromFerroLP = parseFloat(snapshot?.earnedFromFerroLP ?? '0');
      const ferroFactor = parseFloat(snapshot?.ferroFactor ?? '0');
      const lcroAtVvsInCro = parseFloat(snapshot?.lcroAtVvsInCro ?? '0');
      const tLCroBalance = parseFloat(snapshot?.tLCroBalance ?? '0');
      if (i === dates.length - 1) {
        currentFerroFactor = ferroFactor;
        currentFerroLpBalance = ferroLPBalance;
        currentTLcroBalance = tLCroBalance;
        currentVvsCroLcroBalance = parseFloat(snapshot?.vvsLPBalance ?? '0');
      }
      const lCroBalance = parseFloat(snapshot?.lCroBalance ?? '0');
      const earning = {
        pending:
          parseFloat(snapshot?.pendingEarningInCro ?? '0') +
          (includeLpEarnings
            ? parseFloat(snapshot?.earnedFromFerroLP ?? '0')
            : 0),
        date: dates[i],
        lifetime:
          parseFloat(snapshot?.lifeTimeEarningInCro ?? '0') +
          (includeLpEarnings
            ? parseFloat(snapshot?.earnedFromFerroLP ?? '0')
            : 0),
        updated,
        lCroBalance,
        lcroHolding:
          lCroBalance + ferroFactor * ferroLPBalance + lcroAtVvsInCro,
        ferroLPBalance,
        earnedFromFerroLP,
        ferroFactor,
        lcroAtVvsInCro,
        exchangeRate: updated
          ? parseFloat(snapshot?.exchangeRate)
          : parseFloat(
              rates.length === dates.length
                ? rates[i].exchangeRate
                : // this happens in test net only, missing data
                  rates.find(
                    (r) =>
                      parseInt(r.date) >= Math.round(dates[i].getTime() / 1000),
                  )?.exchangeRate ?? rates[rates.length - 1].exchangeRate,
            ),
      };
      return earning;
    });

    let lastKnownExchangeRate = earnings[0].exchangeRate;
    let lastKnownHolding = earnings[0].lcroHolding;
    let lastKnownLifetime = earnings[0].lifetime;
    let lastKnownPending = earnings[0].pending;
    for (let i = 1; i < earnings.length; i++) {
      if (!earnings[i].updated) {
        const balanceIncrease =
          (earnings[i].exchangeRate - lastKnownExchangeRate) * lastKnownHolding;
        earnings[i].lifetime = lastKnownLifetime + balanceIncrease;
        earnings[i].pending = lastKnownPending + balanceIncrease;
      } else {
        lastKnownExchangeRate = earnings[i].exchangeRate;
        lastKnownHolding = earnings[i].lcroHolding;
        lastKnownLifetime = earnings[i].lifetime;
        lastKnownPending = earnings[i].pending;
      }
    }
    return {
      data: earnings,
      currentFerroFactor,
      currentFerroLpBalance,
      currentTLcroBalance,
      currentVvsCroLcroBalance,
    };
  }, [
    currentFerroFactorAtom,
    currentFerroLpBalanceAtom,
    earningsData?.earningSnapshots,
    exchangeRateData?.liquidCroDailySnapshots,
    includeLpEarnings,
    isCro,
  ]);

  if (
    isCro &&
    (!earningsData?.earningSnapshots.length ||
      !exchangeRateData?.liquidCroDailySnapshots.length)
  ) {
    return {
      isLoading,
      dates: [],
      pendingEarnings: [],
      lifetimeEarnings: [],
      currentFerroFactor: 0,
      currentFerroLpBalance: 0,
      currentTLcroBalance: 0,
      currentVvsCroLcroBalance: 0,
    };
  }

  const result = {
    isLoading,
    dates: data.map((e) => e.date),
    pendingEarnings: data.map((e) => parseUnits(e.pending.toFixed(4))),
    lifetimeEarnings: data.map((e) => parseUnits(e.lifetime.toFixed(4))),
    currentFerroFactor,
    currentFerroLpBalance,
    currentTLcroBalance,
    currentVvsCroLcroBalance,
  };
  return result;
};

const useAtomEarningsData = (enabled: boolean, account: string) => {
  const historyClient = useContext(GraphQLContext).historyClientAtom;
  const { data: snapshots } = useQuery(
    getSnapshotsAtom({
      graphQlClient: historyClient,
      address: account,
      first: 1,
    }),
  );
  const currentFerroFactor = snapshots?.[0]?.ferroFactor;
  const ferroLpBalance = snapshots?.[0]?.ferroLPBalance
    ? parseFloat(snapshots?.[0]?.ferroLPBalance)
    : undefined;

  return {
    currentFerroFactor: parseFloat(currentFerroFactor ?? '0'),
    currentFerroLpBalance: ferroLpBalance,
  };
};

export default useEarningsData;
